import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Silent token refresh: on a 401, try /auth/refresh once (uses the long-lived
// refresh cookie) and retry the original request. Keeps users signed in.
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/me");
    if (status === 401 && !original._retry && !isAuthRoute) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(original);
      } catch (e) {
        refreshPromise = null;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Waitlist
export const joinWaitlist = async (payload) => (await api.post("/waitlist", payload)).data;
export const getWaitlist = async () => (await api.get("/waitlist")).data;
export const getWaitlistCount = async () => (await api.get("/waitlist/count")).data;

// Auth
export const apiLogin = async (email, password) => (await api.post("/auth/login", { email, password })).data;
export const apiRegister = async (payload) => (await api.post("/auth/register", payload)).data;
export const apiLogout = async () => (await api.post("/auth/logout")).data;
export const apiMe = async () => (await api.get("/auth/me")).data;
export const apiRefresh = async () => (await api.post("/auth/refresh")).data;
export const validateGstin = async (gstin) => (await api.post("/auth/validate-gstin", { gstin })).data;

// Cases (multiple invoice lines per case)
export const createCase = async (payload) => (await api.post("/cases", payload)).data;
export const listCases = async () => (await api.get("/cases")).data;
export const getCase = async (id) => (await api.get(`/cases/${id}`)).data;
export const updateCase = async (id, payload) => (await api.patch(`/cases/${id}`, payload)).data;
export const setCaseStage = async (id, stage_index) => (await api.patch(`/cases/${id}/stage`, { stage_index })).data;
export const markServed = async (id) => (await api.post(`/cases/${id}/serve`)).data;
export const getNoticeDraft = async (id) => (await api.get(`/cases/${id}/notice/draft`)).data;
export const saveNotice = async (id, data) => (await api.post(`/cases/${id}/notice/save`, data)).data;
export const reopenCase = async (id) => (await api.post(`/cases/${id}/reopen`)).data;
export const listBuyers = async () => (await api.get("/buyers")).data;
export const importInvoicesCsv = async (id, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post(`/cases/${id}/invoices/import`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
  })).data;
};
export const addInvoiceLine = async (id, payload) => (await api.post(`/cases/${id}/invoices`, payload)).data;
export const updateInvoiceLine = async (id, lineId, payload) => (await api.patch(`/cases/${id}/invoices/${lineId}`, payload)).data;
export const deleteInvoiceLine = async (id, lineId) => (await api.delete(`/cases/${id}/invoices/${lineId}`)).data;
export const uploadLineFile = async (id, lineId, kind, file, onProgress) => {
  const fd = new FormData();
  fd.append("kind", kind);
  fd.append("file", file);
  return (await api.post(`/cases/${id}/invoices/${lineId}/files`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
  })).data;
};
export const deleteFile = async (id) => (await api.delete(`/files/${id}`)).data;
export const fileDownloadUrl = (id) => `${API}/files/${id}/download`;
export const sendNoticeEmail = async (id, data) => (await api.post(`/cases/${id}/notice/email`, data)).data;
